import type { TireHotelEventRecord } from '@navio/nsdk';
import { TireHotelEventType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiCard, NoddiSpinner, usePaginationQueryParam } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

type Props = {
  tireHotelId: number;
};

const EVENT_META: Record<TireHotelEventType, { icon: NoddiIconName; phrase: string }> = {
  [TireHotelEventType.CREATED]: { icon: 'Hotel', phrase: 'created the tire hotel' },
  [TireHotelEventType.ACTIVATED]: { icon: 'CircleCheck', phrase: 'activated the tire hotel' },
  [TireHotelEventType.RENEWED]: { icon: 'RefreshCcw', phrase: 'renewed the tire hotel' },
  [TireHotelEventType.CANCELLED]: { icon: 'XCircle', phrase: 'canceled the tire hotel' },
  [TireHotelEventType.TERMINATED]: { icon: 'XCircle', phrase: 'terminated the tire hotel' },
  [TireHotelEventType.WHEEL_SET_DETACHED]: { icon: 'Link', phrase: 'detached a wheel set' },
  [TireHotelEventType.CONTRACT_UPDATED]: { icon: 'FileText', phrase: 'updated the contract' },
  [TireHotelEventType.WHEEL_SET_ATTACHED]: { icon: 'Link', phrase: 'attached a wheel set' },
  [TireHotelEventType.PREFERRED_FACILITY_CHANGED]: {
    icon: 'Building2',
    phrase: 'changed the preferred facility'
  },
  [TireHotelEventType.IN_STOCK_SEASON_CHANGED]: { icon: 'Snowflake', phrase: 'updated the in-stock season' },
  [TireHotelEventType.EXCLUDED_FROM_RECALL_CHANGED]: {
    icon: 'Star',
    phrase: 'updated recall exclusion'
  },
  [TireHotelEventType.REHOME_COMPLETED]: { icon: 'ArrowLeftRight', phrase: 'completed an ownership transfer' },
  [TireHotelEventType.CAR_CHANGE_COMPLETED]: { icon: 'Car', phrase: 'changed the car' },
  [TireHotelEventType.WHEEL_SET_COLLECTED_AT_LOCATION]: {
    icon: 'MapPin',
    phrase: 'collected wheels at a tire hotel location'
  }
};

export const TireHotelActivityTab = ({ tireHotelId }: Props) => {
  const { pagination } = usePaginationQueryParam({ paramKeyPrefix: 'activity' });

  const { data, isPending, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedTireHotelEvents,
    input: { tireHotelId, ...pagination },
    queryConfig: { placeholderData: keepPreviousData }
  });

  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }

  if (isPending) {
    return (
      <div className='flex items-center justify-center py-10'>
        <NoddiSpinner />
      </div>
    );
  }

  const activities = data?.results ?? [];

  return (
    <NoddiCard title='Activity' icon='Activity' iconTile size='small'>
      {activities.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No activity yet.</p>
      ) : (
        <div className='flex flex-col gap-4'>
          {activities.map((activity) => (
            <ActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}
    </NoddiCard>
  );
};

const ActivityRow = ({ activity }: { activity: TireHotelEventRecord }) => {
  const actorName = activity.actorName ?? 'System';
  const timestamp = noddiFormatDate(activity.createdAt, NoddiDateFormats.DATE_TIME);
  const meta = EVENT_META[activity.eventType];
  const icon = meta?.icon ?? 'Activity';
  const phrase = meta?.phrase ?? activity.eventType.toLowerCase().replaceAll('_', ' ');
  const detail = activity.detail?.trim() || null;
  const locationLine = activity.tireHotelLocationId != null ? `Location #${activity.tireHotelLocationId}` : null;
  const carLine =
    activity.carFromId != null || activity.carToId != null
      ? `Car #${activity.carFromId ?? '-'} → #${activity.carToId ?? '-'}`
      : null;

  return (
    <div className='flex items-start gap-3'>
      <div className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted')}>
        <NoddiIcon name={icon} size='small' className='text-muted-foreground' />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm'>
          <span className='font-medium'>{actorName}</span>
          <span className='text-muted-foreground'>{phrase}</span>
          <span className='ml-auto text-xs text-muted-foreground'>{timestamp}</span>
        </div>
        {detail ? <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{detail}</p> : null}
        {locationLine ? <p className='text-xs text-muted-foreground'>{locationLine}</p> : null}
        {carLine ? <p className='text-xs text-muted-foreground'>{carLine}</p> : null}
      </div>
    </div>
  );
};
